import {
  LIST_FEATURE_FLAGS,
  useFeatureFlagWithState,
} from '@diamond/shared/utils';
import {
  useAuthStore,
  usePwaInstallListener,
  usePwaStore,
} from '@diamond/sol-buyer-context';
import dynamic from 'next/dynamic';

import { OfflineBanner } from './OfflineBanner';
import { PwaBootSplash } from './PwaBootSplash';
import { usePwaHeadMetadata } from './usePwaHeadMetadata';
import { usePwaNetworkStatus } from './usePwaNetworkStatus';
import { usePwaServiceWorker } from './usePwaServiceWorker';
import { useWebPushNotifications } from './useWebPushNotifications';
import { WebPushPermissionBanner } from './WebPushPermissionBanner';

const PwaInstallBanner = dynamic(
  () => import('./PwaInstallBanner').then((module) => module.PwaInstallBanner),
  { ssr: false }
);

export function PwaRuntime() {
  const accessToken = useAuthStore((state) => state.access_token);
  const isPwaOffline = usePwaStore((state) => state.isPwaOffline);
  const isInstallBannerVisible = usePwaStore(
    (state) => state.isInstallBannerVisible
  );
  const { value: isPwaEnabled, loading: isPwaFlagLoading } =
    useFeatureFlagWithState(LIST_FEATURE_FLAGS.FF_SOL_BUYER_PWA, false);
  const { value: isWebPushEnabled, loading: isWebPushFlagLoading } =
    useFeatureFlagWithState(LIST_FEATURE_FLAGS.FF_SOL_BUYER_WEB_PUSH, false);
  const shouldRunPwa = isPwaEnabled && Boolean(accessToken);
  const shouldRunWebPush = Boolean(accessToken) && isWebPushEnabled;
  const isWebPushLoading = isWebPushFlagLoading;
  const shouldDeleteToken =
    Boolean(accessToken) && !isWebPushEnabled && !isWebPushLoading;

  usePwaHeadMetadata(isPwaEnabled, isPwaFlagLoading);
  usePwaInstallListener(isPwaFlagLoading || isPwaEnabled);
  usePwaNetworkStatus(shouldRunPwa);
  usePwaServiceWorker(shouldRunPwa, isPwaFlagLoading);
  const { canPrompt, canShowRecovery, requestPermission } =
    useWebPushNotifications({
      isEnabled: shouldRunWebPush,
      isLoading: isWebPushLoading,
      shouldDeleteToken,
    });

  return (
    <>
      <PwaBootSplash />
      {isPwaFlagLoading || !isPwaEnabled ? null : (
        <>
          {shouldRunPwa && isPwaOffline ? <OfflineBanner /> : null}
          {!isPwaOffline ? (
            <PwaInstallBanner isPwaEnabled={isPwaEnabled} />
          ) : null}
        </>
      )}
      <WebPushPermissionBanner
        isVisible={
          shouldRunWebPush &&
          !isWebPushLoading &&
          (!isPwaEnabled || !isInstallBannerVisible) &&
          (canPrompt || canShowRecovery)
        }
        isBlocked={canShowRecovery}
        onEnable={requestPermission}
      />
    </>
  );
}
